import { fitTemplate, workingDaysInWindow } from "./scale";
import { dayKey, isWorkingDay, parseDay, scheduleTemplate, type DateBase } from "./schedule";

const NO_HOLIDAYS: ReadonlySet<string> = new Set();
const HOURS_PER_DAY = 8;

// What the wizard and the server both need from a template: no checklists, files or articles.
export type TemplateOutline = {
  id: number;
  name: string;
  buckets: {
    name: string;
    startDay: number;
    durationDays: number;
    cards: { title: string; effortHours: number | null; startBase: DateBase; startDay: number | null; dueBase: DateBase; dueDay: number | null; roleLabelIds: number[] }[];
  }[];
};

export type RoleMapping = { roleLabelId: number; userIds: number[]; customerNames: string[] };

/** A bucket window the PM changed in the review step, by bucket position. */
export type BucketDates = { index: number; start: string; end: string };

export type PlannedCard = { title: string; effortHours: number | null; start: Date; due: Date; roleLabelIds: number[]; userIds: number[] };
export type PlannedBucket = { name: string; start: Date; end: Date; cards: PlannedCard[] };

export type ProjectPlan = {
  buckets: PlannedBucket[];
  totalEffort: number;
  /** Working days between start and go-live, null without a go-live date. */
  windowDays: number | null;
  /** Role ids used by cards that nobody is mapped to. */
  unstaffedRoleIds: number[];
  warnings: string[];
};

/**
 * Project dates skip weekends only: a project spans people in several countries, so public holidays
 * never move them. With a go-live date the template is stretched or squeezed to end on it.
 */
export function planProject(
  template: TemplateOutline | null,
  input: { startDate: string; goLiveDate?: string | null; bucketDates?: BucketDates[]; budgetHours: number | null; roles: RoleMapping[]; roleNames?: Map<number, string>; today?: Date },
): ProjectPlan {
  const start = parseDay(input.startDate);
  const warnings: string[] = [];
  if (!start) return { buckets: [], totalEffort: 0, windowDays: null, unstaffedRoleIds: [], warnings };
  const holidays = NO_HOLIDAYS;
  const goLive = input.goLiveDate ? parseDay(input.goLiveDate) : null;
  const windowDays = goLive && goLive >= start ? workingDaysInWindow(start, goLive) : null;
  if (goLive && !windowDays) warnings.push("The go-live date must be on or after the start date, on a working day or later.");

  const people = new Map(input.roles.map((r) => [r.roleLabelId, r]));
  const fitted = template && windowDays ? fitTemplate(template.buckets, windowDays) : (template?.buckets ?? []);
  const schedule = template ? scheduleTemplate(fitted, start, holidays) : [];
  const buckets = (template?.buckets ?? []).map<PlannedBucket>((b, bi) => ({
    name: b.name,
    start: schedule[bi].start,
    end: schedule[bi].end,
    cards: b.cards.map((c, ci) => ({
      title: c.title,
      effortHours: c.effortHours,
      start: schedule[bi].cards[ci].start,
      due: schedule[bi].cards[ci].due,
      roleLabelIds: c.roleLabelIds,
      userIds: [...new Set(c.roleLabelIds.flatMap((id) => people.get(id)?.userIds ?? []))],
    })),
  }));

  for (const o of input.bucketDates ?? []) {
    const b = buckets[o.index];
    const from = parseDay(o.start);
    const to = parseDay(o.end);
    if (!b || !from || !to || to < from) continue;
    b.start = from;
    b.end = to;
    for (const c of b.cards) {
      c.start = c.start < from ? from : c.start > to ? to : c.start;
      c.due = c.due > to ? to : c.due < c.start ? c.start : c.due;
    }
  }

  const totalEffort = buckets.reduce((sum, b) => sum + b.cards.reduce((s, c) => s + (c.effortHours ?? 0), 0), 0);
  const usedRoles = [...new Set(buckets.flatMap((b) => b.cards.flatMap((c) => c.roleLabelIds)))];
  const unstaffedRoleIds = usedRoles.filter((id) => {
    const m = people.get(id);
    return !m || (m.userIds.length === 0 && m.customerNames.length === 0);
  });

  const name = (id: number) => input.roleNames?.get(id) ?? "A role";
  if (input.budgetHours !== null && totalEffort > input.budgetHours) {
    warnings.push(`The tasks add up to ${totalEffort} h, more than the ${input.budgetHours} h budget.`);
  }
  for (const id of unstaffedRoleIds) warnings.push(`${name(id)} is used by tasks but nobody is mapped to it.`);
  const noOwner = buckets.flatMap((b) => b.cards).filter((c) => c.roleLabelIds.length > 0 && c.userIds.length === 0 && !c.roleLabelIds.some((id) => (people.get(id)?.customerNames.length ?? 0) > 0));
  if (noOwner.length > 0 && unstaffedRoleIds.length === 0) warnings.push(`${noOwner.length} tasks have no eVia person assigned.`);
  const staff = new Set(input.roles.flatMap((r) => r.userIds));
  if (windowDays && totalEffort > windowDays * HOURS_PER_DAY * Math.max(staff.size, 1)) {
    const fits = windowDays * HOURS_PER_DAY * Math.max(staff.size, 1);
    warnings.push(`The tasks add up to ${totalEffort} h, but ${windowDays} working days with ${Math.max(staff.size, 1)} ${staff.size > 1 ? "people" : "person"} allow about ${fits} h. Move the go-live date or add people.`);
  }
  if (!isWorkingDay(start, holidays)) warnings.push(`${dayKey(start)} is not a working day. The project starts on ${dayKey(buckets[0]?.start ?? start)}.`);
  if (input.today && dayKey(start) < dayKey(input.today)) warnings.push("The start date is in the past, so some tasks will already be overdue.");
  if (!template) warnings.push("No template: the project starts empty. Add buckets and tasks on the board.");

  return { buckets, totalEffort, windowDays, unstaffedRoleIds, warnings };
}
